import { Plugin } from '@ckeditor/ckeditor5-core';
import { toWidget, toWidgetEditable } from '@ckeditor/ckeditor5-widget';
import InsertExpertTipCommand from './expert-tip-command';
export default class ExpertTipEditing extends Plugin {
    init() {
        this.editor.commands.add('insertExpertTip', new InsertExpertTipCommand(this.editor));
        this._defineSchema();
        this._defineConverters();
        this._definePostFixer();
    }
    _defineSchema() {
        const schema = this.editor.model.schema;
        schema.register('expertTip', {
            isObject: true,
            allowWhere: '$block',
            allowContentOf: '$root',
            allowAttributes: ['authorId', 'class'],
        });
        schema.register('expertTipAuthorName', {
            isLimit: true,
            allowIn: 'expertTip',
            allowContentOf: '$block',
        });
        schema.register('expertTipAuthorDesc', {
            isLimit: true,
            allowIn: 'expertTip',
            allowContentOf: '$block',
        });
    }
    _defineConverters() {
        const conversion = this.editor.conversion;
        conversion.for('upcast').elementToElement({
            model: (viewElement, { writer }) => {
                const authorId = viewElement.getAttribute('data-author-id');
                const mod = authorId ? 'expert-tip_with-author' : '';
                return writer.createElement('expertTip', {
                    authorId: authorId || null,
                    class: `expert-tip ${mod}`,
                });
            },
            view: 'x-expert-tip',
        });
        conversion.for('dataDowncast').elementToElement({
            model: 'expertTip',
            view: (modelElement, { writer }) => {
                return writer.createContainerElement('x-expert-tip', {
                    'data-author-id': modelElement.getAttribute('authorId') || null,
                });
            },
        });
        conversion.for('editingDowncast').elementToElement({
            model: 'expertTip',
            view: (model, { writer }) => {
                const authorId = model.getAttribute('authorId');
                const mod = authorId ? 'expert-tip_with-author' : '';
                const element = writer.createEditableElement('div', {
                    class: `expert-tip ${mod}`,
                    'data-author-id': authorId || null,
                });
                return toWidgetEditable(element, writer, { label: 'expert tip' });
            },
        });
        // Expert tip author name
        conversion.for('upcast').elementToElement({
            model: 'expertTipAuthorName',
            view: 'x-expert-tip-author-name',
        });
        conversion.for('dataDowncast').elementToElement({
            model: 'expertTipAuthorName',
            view: 'x-expert-tip-author-name',
        });
        conversion.for('editingDowncast').elementToElement({
            model: 'expertTipAuthorName',
            view: (model, { writer }) => {
                const element = writer.createContainerElement('div', {
                    class: 'expert-tip__author-name',
                });
                return toWidget(element, writer);
            },
        });
        // Expert tip author title
        conversion.for('upcast').elementToElement({
            model: 'expertTipAuthorDesc',
            view: 'x-expert-tip-author-title',
        });
        conversion.for('dataDowncast').elementToElement({
            model: 'expertTipAuthorDesc',
            view: 'x-expert-tip-author-title',
        });
        conversion.for('editingDowncast').elementToElement({
            model: 'expertTipAuthorDesc',
            view: (model, { writer }) => {
                const element = writer.createContainerElement('div', {
                    class: 'expert-tip__author-title',
                });
                return toWidget(element, writer);
            },
        });
    }
    _definePostFixer() {
        const editor = this.editor;
        const schema = editor.model.schema;
        // Postfixer which cleans incorrect model states connected with expertTips.
        editor.model.document.registerPostFixer((writer) => {
            const changes = editor.model.document.differ.getChanges();
            for (const entry of changes) {
                if (entry.type == 'insert') {
                    const element = entry.position.nodeAfter;
                    if (!element) {
                        // We are inside a text node.
                        continue;
                    }
                    if (element.is('element', 'expertTip') && element.isEmpty) {
                        // Added an empty expertTip - remove it.
                        writer.remove(element);
                        return true;
                    }
                    else if (element.is('element', 'expertTip') &&
                        !schema.checkChild(entry.position, element)) {
                        // Added a expertTip in incorrect place - most likely inside another expertTip. Unwrap it
                        // so the content inside is not lost.
                        writer.unwrap(element);
                        return true;
                    }
                    else if (element.is('element')) {
                        // Just added an element. Check its children to see if there are no nested expertTips somewhere inside.
                        const range = writer.createRangeIn(element);
                        for (const child of range.getItems()) {
                            if (child.is('element', 'expertTip') &&
                                !schema.checkChild(writer.createPositionBefore(child), child)) {
                                writer.unwrap(child);
                                return true;
                            }
                        }
                    }
                }
                else if (entry.type == 'remove') {
                    const parent = entry.position.parent;
                    if (parent.is('element', 'expertTip') && parent.isEmpty) {
                        // Something got removed and now expertTip is empty. Remove the expertTip as well.
                        writer.remove(parent);
                        return true;
                    }
                }
            }
            return false;
        });
    }
}
